import Box from 'assets/components/box/Box';
import Button from 'assets/components/button/Button';
import LinkButton from 'assets/components/button/LinkButton';
import ThemeButtonCircle from 'assets/components/button/themes/Theme.Button.Circle';
import DataMap from 'assets/components/dataMap/DataMap';
import FormatValue from 'assets/components/formatValue/FormatValue';
import Icon from 'assets/components/icon/Icon';
import Page from 'assets/components/page/Page';
import ThemePageError from 'assets/components/page/themes/Theme.Page.Error';
import ThemePageList from 'assets/components/page/themes/Theme.Page.List';
import Pagination from 'assets/components/pagination/Pagination';
import ThemePaginationRounded from 'assets/components/pagination/themes/Theme.Pagination.Rounded';
import ThemeTableSimple from 'assets/components/table/themes/Theme.Table.Simple';
import ThemeTagRounded from 'assets/components/tag/themes/Theme.Tag.Rounded';
import { getLocales } from 'assets/locales/Locale';
import Theme from 'assets/themes/Theme.Common';
import ThemeWrapperStack from 'assets/themes/wrappers/Theme.Wrapper.Stack';
import { defaultDateTimeFormat, localizeDate } from 'assets/utils/data/Date';
import React from 'react';
import { isDesktop, isMobile } from 'react-device-detect';
import { ContractRefundInvoiceAction, useContractRefundInvoiceProvider } from '../Sales.ContractRefundInvoice.Hooks';
import ContractRefundInvoiceFilters from './Sales.ContractRefundInvoice.Filters';
import ContractRefundInvoiceInfo from './Sales.ContractRefundInvoice.Info';

export default function ContractRefundInvoiceList() {
  const { lang, translate } = getLocales();
  const { listApi, pageRouter, permissions, generateInvoiceDocument, isGeneratingDocument } =
    useContractRefundInvoiceProvider();

  return !listApi.payload && !listApi.isExecuting && listApi.apiCounter > 0 ? (
    <Page
      class={ThemePageError}
      sidebar={<Icon class="fas-clipboard-list" />}
      header={lang.contractRefundInvoices}
      render={lang.noInfo}
    />
  ) : (
    <Page
      class={ThemePageList}
      sidebar={<ContractRefundInvoiceInfo />}
      isLoading={listApi.isExecuting}
      header={
        <>
          <h2 style={{ marginRight: 'auto' }} children={lang.contractRefundInvoices} />
          <Box class={ThemeWrapperStack}>{isMobile && <ContractRefundInvoiceFilters />}</Box>
        </>
      }
      subHeader={isDesktop && <ContractRefundInvoiceFilters />}
      render={
        <table key="list" className={ThemeTableSimple}>
          <thead>
            <tr>
              <th>{lang.invoiceNumber}</th>
              <th>{`${lang.refund} #`}</th>
              <th>{lang.paymentMethod}</th>
              <th>{lang.dealer}</th>
              <th>{lang.amount}</th>
              <th>{lang.gstHst}</th>
              <th>{lang.pst}</th>
              <th>{lang.total}</th>
              <th>{lang.remittance}</th>
              <th>{lang.createdAt}</th>
              <th data-actions>{lang.actions}</th>
            </tr>
          </thead>
          <tbody>
            <DataMap
              data={listApi.payload?.data}
              render={({ data: contractRefundInvoice }) => {
                const isGenerating = isGeneratingDocument(contractRefundInvoice.id);

                return (
                  <React.Fragment key={contractRefundInvoice.id}>
                    <tr style={{ borderTop: `2px solid ${Theme.Color.shadow}` }}>
                      <td>
                        <b>{contractRefundInvoice.number}</b>
                      </td>
                      <td>
                        {contractRefundInvoice.contractRefundDealerPaymentControl?.contractRefund?.jobId ?? 'N/A'}
                      </td>
                      <td>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: Theme.Size.XS }}>
                          <span>
                            {contractRefundInvoice.isCancelled && (
                              <Box
                                class={ThemeTagRounded}
                                style={{ color: Theme.Color.error, backgroundColor: Theme.Color.errorLight }}
                              >
                                <Icon class="fas-check" />
                                &nbsp; {lang.cancelled}
                              </Box>
                            )}
                          </span>
                          <span>
                            {contractRefundInvoice.paymentMethodCode
                              ? translate(contractRefundInvoice.paymentMethodCode)
                              : 'N/A'}
                          </span>
                        </div>
                      </td>
                      <td>
                        {contractRefundInvoice.contractRefundDealerPaymentControl?.contractRefundPayee?.dealer?.name}
                      </td>
                      <td>
                        <FormatValue value={`${(contractRefundInvoice.totalAmountBeforeTax || 0).toFixed(2)}$`} />
                      </td>
                      <td>
                        <FormatValue value={`${(contractRefundInvoice.gstHstAmount || 0).toFixed(2)}$`} />
                      </td>
                      <td>
                        <FormatValue value={`${(contractRefundInvoice.provincialSalesTaxAmount || 0).toFixed(2)}$`} />
                      </td>
                      <td>
                        <b>
                          <FormatValue value={`${(contractRefundInvoice.totalAmount || 0).toFixed(2)}$`} />
                        </b>
                      </td>
                      <td>
                        {/*TODO: Remittance (after total): true/false; true if Invoice is collected for Remittance*/}
                        N/A
                      </td>
                      <td>
                        {contractRefundInvoice.createdBy?.fullName && (
                          <>
                            {contractRefundInvoice.createdBy.fullName} {lang.at}
                            <br />
                          </>
                        )}
                        {contractRefundInvoice.createdAtLocal
                          ? localizeDate(contractRefundInvoice.createdAtLocal, defaultDateTimeFormat)
                          : 'N/A'}
                      </td>
                      <td>
                        <Box class={ThemeWrapperStack}>
                          <Button
                            class={ThemeButtonCircle}
                            media={<Icon class="fas-info-circle" />}
                            onClick={pageRouter.updateParams}
                            data={{
                              id: contractRefundInvoice.id.toString(),
                              action: ContractRefundInvoiceAction.INFO,
                            }}
                            htmlElementProps={{ title: lang.info }}
                            active
                          />
                          <LinkButton
                            class={ThemeButtonCircle}
                            url={contractRefundInvoice.invoiceDocument?.url}
                            media={<Icon class="fas-download" />}
                            target="_blank"
                            disabled={!contractRefundInvoice.invoiceDocument?.url}
                            htmlElementProps={{
                              title: !contractRefundInvoice.invoiceDocument?.url ? lang.missing : lang.download,
                            }}
                            active
                          />
                          <Button
                            class={ThemeButtonCircle}
                            media={<Icon class="fas-envelope" />}
                            onClick={pageRouter.updateParams}
                            data={{
                              id: contractRefundInvoice.id.toString(),
                              action: ContractRefundInvoiceAction.SEND_EMAIL,
                            }}
                            htmlElementProps={{ title: lang.sendEmail }}
                            active
                          />
                          <Button
                            class={ThemeButtonCircle}
                            media={<Icon class="fas-sync-alt" />}
                            onClick={() => generateInvoiceDocument(contractRefundInvoice.id)}
                            htmlElementProps={{ title: lang.regenerateInvoiceDocument }}
                            disabled={!permissions.EDIT_CONTRACTREFUND || isGenerating}
                            active
                          />
                        </Box>
                      </td>
                    </tr>
                  </React.Fragment>
                );
              }}
            />
          </tbody>
        </table>
      }
      footer={
        <Pagination
          class={ThemePaginationRounded}
          count={listApi.payload?.count}
          offset={listApi.body?.offset}
          limit={listApi.body?.limit}
          onLimitChange={(limit) => listApi.execute((body) => ({ ...body, limit, offset: 0 }))}
          onOffsetChange={(offset) => listApi.execute((body) => ({ ...body, offset }))}
        />
      }
    />
  );
}
